import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { gsap } from "@/lib/gsap";
import { usePrefersReducedMotion } from "@/lib/reduced-motion";

/**
 * 끊김 없이 흐르는 가로 로고 루프.
 * react-bits `LogoLoop` 을 이 저장소에 맞게 옮겼어요 — 세로 방향·fadeOut 색상 계산·renderItem 은
 * 쓰는 곳이 없어 걷어냈고, 가장자리 페이드는 배경색을 몰라도 되는 mask 로 처리해요.
 *
 * 트랙 길이는 첫 번째 사본의 폭을 재서 정하므로, 항목 안의 이미지에는 크기가 고정돼 있어야 해요
 * (로드 전후로 폭이 바뀌면 이음매가 어긋나요).
 */

export interface LogoLoopItem {
  /** 항목 내용. 폭이 고정된 요소여야 해요. */
  node: ReactNode;
  /** 목록 항목 식별용 키 겸 스크린리더 라벨. */
  label: string;
  href?: string;
}

export interface LogoLoopProps {
  items: LogoLoopItem[];
  /** px/초. @default 60 */
  speed?: number;
  /** @default "left" */
  direction?: "left" | "right";
  /** 항목 간격(px). @default 32 */
  gap?: number;
  /** @default true */
  pauseOnHover?: boolean;
  /** 양 끝을 투명하게 흐려요. @default true */
  fade?: boolean;
  ariaLabel: string;
  className?: string;
}

const MIN_COPIES = 2;
// 컨테이너를 채우고도 남을 만큼 사본을 더 둬서, 되감기는 순간에도 빈 구간이 안 보이게 해요.
const COPY_HEADROOM = 2;
// 목표 속도(정지 ↔ 이동)를 따라가는 시간상수(초). hover 로 멈출 때 급정거하지 않아요.
const SMOOTH_TAU = 0.25;

export function LogoLoop({
  items,
  speed = 60,
  direction = "left",
  gap = 32,
  pauseOnHover = true,
  fade = true,
  ariaLabel,
  className,
}: LogoLoopProps) {
  const reduce = usePrefersReducedMotion();
  const containerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const seqRef = useRef<HTMLUListElement>(null);
  // hover 로 속도가 바뀌어도 위치는 이어져야 해서 effect 밖에 들고 있어요.
  const offsetRef = useRef(0);
  const velocityRef = useRef(0);

  const [seqWidth, setSeqWidth] = useState(0);
  const [copyCount, setCopyCount] = useState(MIN_COPIES);
  const [hovered, setHovered] = useState(false);

  const measure = useCallback(() => {
    const sequenceWidth = seqRef.current?.getBoundingClientRect().width ?? 0;
    if (sequenceWidth <= 0) return;
    setSeqWidth(Math.ceil(sequenceWidth));
    const containerWidth = containerRef.current?.clientWidth ?? 0;
    setCopyCount(Math.max(MIN_COPIES, Math.ceil(containerWidth / sequenceWidth) + COPY_HEADROOM));
  }, []);

  useEffect(() => {
    measure();
    const ro = new ResizeObserver(measure);
    if (containerRef.current) ro.observe(containerRef.current);
    if (seqRef.current) ro.observe(seqRef.current);
    return () => ro.disconnect();
  }, [measure]);

  const targetVelocity = (direction === "left" ? 1 : -1) * speed;

  useEffect(() => {
    const track = trackRef.current;
    // reduced-motion 이면 애니메이션 자체를 걸지 않고 첫 사본이 보이는 정지 상태로 둬요.
    if (!track || reduce || seqWidth <= 0) return;

    // GSAP 의 ticker 에 얹어요 — 페이지의 다른 애니메이션과 rAF 루프를 하나로 공유하고,
    // 탭이 백그라운드에 있는 동안엔 GSAP 이 알아서 재워줘요.
    const frame = (_time: number, deltaMs: number) => {
      const dt = deltaMs / 1000;

      const target = hovered && pauseOnHover ? 0 : targetVelocity;
      velocityRef.current += (target - velocityRef.current) * (1 - Math.exp(-dt / SMOOTH_TAU));

      const next = offsetRef.current + velocityRef.current * dt;
      offsetRef.current = ((next % seqWidth) + seqWidth) % seqWidth;
      track.style.transform = `translate3d(${-offsetRef.current}px, 0, 0)`;
    };
    gsap.ticker.add(frame);
    return () => gsap.ticker.remove(frame);
  }, [reduce, seqWidth, hovered, pauseOnHover, targetVelocity]);

  const copies = useMemo(
    () =>
      Array.from({ length: copyCount }, (_, copyIndex) => (
        <ul
          key={`copy-${copyIndex}`}
          ref={copyIndex === 0 ? seqRef : undefined}
          // 사본은 시각적 반복일 뿐이라 첫 번째만 읽히게 해요.
          aria-label={copyIndex === 0 ? ariaLabel : undefined}
          aria-hidden={copyIndex > 0}
          className="flex shrink-0 items-center"
          style={{ gap, paddingRight: gap }}
        >
          {items.map((item) => (
            <li key={item.label} className="shrink-0">
              {item.href ? (
                <a
                  href={item.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={item.label}
                  className="inline-flex items-center rounded-md transition-opacity duration-200 ease-standard hover:opacity-80"
                  // 사본 링크는 키보드 순회에서 빠져야 같은 링크를 여러 번 밟지 않아요.
                  tabIndex={copyIndex > 0 ? -1 : undefined}
                >
                  {item.node}
                </a>
              ) : (
                item.node
              )}
            </li>
          ))}
        </ul>
      )),
    [copyCount, items, gap, ariaLabel],
  );

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden ${
        fade ? "[mask-image:linear-gradient(90deg,transparent,black_6%,black_94%,transparent)]" : ""
      } ${className ?? ""}`}
    >
      <div
        ref={trackRef}
        className="flex w-max will-change-transform"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {copies}
      </div>
    </div>
  );
}

export default LogoLoop;
